import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

/** Types mirror backend/src/modules/directory (Wave C1). */

export type DirectoryMode = 'live' | 'mock' | 'off';

export interface DirectoryConfigProblem {
  key: string;
  message: string;
}

export interface DirectorySyncCounts {
  organizations: { checked: number; updated: number };
  people: {
    roster: number;
    created: number;
    bound: number;
    updated: number;
    unchanged: number;
    reactivated: number;
    deactivated: number;
    conflicts: number;
  };
  deactivations: { checked: number; deactivated: number };
}

export interface DirectoryRun {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'success' | 'error' | string;
  trigger: 'manual' | 'cron' | 'webhook';
  mode: string;
  counts: DirectorySyncCounts;
  error: string | null;
}

export interface DirectoryStatus {
  mode: DirectoryMode;
  connected: boolean;
  endpoint: string;
  appId: string;
  hasServiceKey: boolean;
  authInternalUrl: string | null;
  provisionOnLogin: boolean;
  problems: DirectoryConfigProblem[];
  organization: { authOrgId: string; name: string | null; slug: string | null };
  lastSyncAt: string | null;
  counts: { people: number; linked: number; localOnly: number; active: number; inactive: number };
  lastRun: DirectoryRun | null;
  webhook: { path: string; enabled: boolean; dormant: boolean; reason: string };
}

export type DirectoryFailureReason =
  | 'not_configured'
  | 'disabled'
  | 'dns'
  | 'refused'
  | 'timeout'
  | 'tls'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'server'
  | 'bad_response'
  | 'network';

export interface DirectoryTestResult {
  ok: boolean;
  mode: string;
  endpoint: string;
  reason?: DirectoryFailureReason;
  detail?: string;
  checkedAt: string;
  latencyMs?: number;
}

export interface DirectorySyncResult {
  runId: string | null;
  status: 'success' | 'error' | 'skipped';
  startedAt: string;
  finishedAt: string;
  counts: DirectorySyncCounts;
  errors: string[];
  mode: string;
  trigger: string;
}

export interface DirectoryPerson {
  id: string;
  name: string;
  email: string;
  type: 'STAFF' | 'CONTRACTOR';
  isActive: boolean;
  authUserId: string | null;
  picture: string | null;
  appRole: string | null;
  endDate: string | null;
  linked: boolean;
}

const KEY = ['directory'] as const;

export function useDirectoryStatus() {
  return useQuery({
    queryKey: [...KEY, 'status'],
    queryFn: () => api.get<DirectoryStatus>('/directory/status'),
  });
}

export function useDirectoryRuns() {
  return useQuery({
    queryKey: [...KEY, 'runs'],
    queryFn: () => api.get<{ runs: DirectoryRun[] }>('/directory/runs'),
    refetchInterval: (query) =>
      query.state.data?.runs.some((run) => run.status === 'running') ? 1500 : false,
  });
}

export function useDirectoryPeople() {
  return useQuery({
    queryKey: [...KEY, 'people'],
    queryFn: () => api.get<{ people: DirectoryPerson[] }>('/directory/people'),
  });
}

/* ---------------------------------------------------- clients from accounts */

export type ClientImportAction = 'linked' | 'link-by-name' | 'create';

export interface ClientImportRow {
  orgId: string;
  name: string;
  slug: string;
  logo: string | null;
  address: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  phone: string | null;
  parentOrgId: string | null;
  action: ClientImportAction;
  clientId: string | null;
  clientName: string | null;
}

export interface ClientImportPreview {
  ok: boolean;
  reason: string | null;
  rows: ClientImportRow[];
  otherAgencies: number;
  counts: { linked: number; linkByName: number; create: number };
}

export interface ClientImportResult {
  ok: boolean;
  reason: string | null;
  created: ClientImportRow[];
  linked: ClientImportRow[];
  unchanged: number;
  refused: string[];
}

/** Accounts' CLIENT organizations for this agency and what importing each would do. */
export function useDirectoryClients() {
  return useQuery({
    queryKey: [...KEY, 'clients'],
    queryFn: () => api.get<ClientImportPreview>('/directory/clients'),
  });
}

export interface AccountsOrgMember {
  userId: string;
  name: string;
  email: string;
  picture: string | null;
}

/** The people of one Accounts CLIENT org — offered as the billing contact on New client. */
export function useAccountsOrgMembers(orgId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'clients', orgId, 'members'],
    queryFn: () =>
      api.get<{ ok: boolean; reason: string | null; members: AccountsOrgMember[] }>(
        `/directory/clients/${encodeURIComponent(orgId ?? '')}/members`,
      ),
    enabled: orgId !== null,
  });
}

export function useImportDirectoryClients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orgIds: string[]) =>
      api.post<ClientImportResult>('/directory/clients/import', { orgIds }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...KEY, 'clients'] });
      // New or newly-linked clients change /clients and every picker that lists them.
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['hub'] });
    },
  });
}

export function useTestDirectoryConnection() {
  return useMutation({
    mutationFn: () => api.post<DirectoryTestResult>('/directory/test-connection'),
  });
}

export function useRunDirectorySync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<DirectorySyncResult>('/directory/sync'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEY });
      // A sync can rename the org or (de)activate people — refresh the screens
      // that read them so the app never contradicts itself.
      void queryClient.invalidateQueries({ queryKey: ['team'] });
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

/** "3 minutes ago" / "in 2 hours" — small enough not to warrant a dependency. */
export function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const deltaSeconds = Math.round((then - Date.now()) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY],
  ];
  let value = deltaSeconds;
  for (const [unit, size] of units) {
    if (Math.abs(value) < size) {
      return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(Math.round(value), unit);
    }
    value /= size;
  }
  return 'never';
}

/** Human explanation for each precise failure reason. */
export const FAILURE_EXPLANATIONS: Record<DirectoryFailureReason, string> = {
  not_configured: 'Directory is not configured — fill in ACCOUNTS_API_URL and SERVICE_API_KEY.',
  disabled: 'DIRECTORY_MODE=off — directory sync is turned off for this deployment.',
  dns: 'DNS lookup failed — the host in ACCOUNTS_API_URL does not resolve.',
  refused: 'Connection refused — nothing is listening on that host/port.',
  timeout: 'Timed out waiting for a response — the service is slow, or unreachable behind a firewall.',
  tls: 'TLS certificate was rejected — check the certificate chain or the protocol (http vs https).',
  unauthorized: 'Accounts rejected SERVICE_API_KEY (401) — the key is wrong or has been rotated.',
  forbidden: 'Accounts refused this resource (403) — the app may not be entitled to this organization.',
  not_found: 'Accounts returned 404 — wrong path, or the org/app id does not exist there.',
  server: 'Accounts returned a 5xx — the service is up but failing.',
  bad_response: 'The response was not the JSON we expect — ACCOUNTS_API_URL may point at the web app, not the API.',
  network: 'Network error before a response arrived.',
};
